/**
 * Rute definite in COD (nu pe fisiere) si search params validate cu Zod.
 *
 * Starea din URL e cerinta aici, nu preferinta: „trimite-mi ce vezi acum" trebuie sa functioneze
 * intre colege, iar un filtru pierdut la refresh transforma un instrument de trezorerie intr-o
 * ghicitoare. Fiecare camp are `.catch(undefined)`, deci un URL stricat de un client de email
 * degradeaza gratios in loc sa dea ecran alb.
 *
 * Tot ce nu e ecranul zilei se incarca lazy: bugetul e <=180 KB JS gzip pe ruta initiala.
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { todayInBucharest } from '@evomag/core/format';
import { Shell } from './app/Shell.tsx';
import { PlanPlatiZi } from './features/plata-zi/PlanPlatiZi.tsx';
import { CODURI_STARE } from './lib/stari.ts';

// --- fragmente reutilizabile de schema ---------------------------------------------------------

const text = z.string().min(1).optional().catch(undefined);
const listaText = z.array(z.string().min(1)).optional().catch(undefined);
const listaStari = z.array(z.enum(CODURI_STARE)).optional().catch(undefined);
const densitate = z.enum(['compact', 'standard', 'relaxat']).optional().catch(undefined);
const numar = z.coerce.number().finite().optional().catch(undefined);
const valute = z.array(z.enum(['RON', 'EUR', 'USD', 'HUF', 'GBP'])).optional().catch(undefined);
/**
 * `z.coerce.boolean()` NU se foloseste: ar transforma `"false"` in `true` (orice string nevid e
 * truthy in JS), adica exact inversul intenției din URL.
 */
const boolean = z
  .preprocess((v) => (v === 'true' ? true : v === 'false' ? false : v), z.boolean())
  .optional()
  .catch(undefined);

/** `/zi/:data` — filtrele grilei zilnice. */
const cautareZi = z.object({
  /** Runda de plata (`principala`, `dimineata`, `edl`, `mirela`…) — fostele coloane ad-hoc. */
  runda: text,
  stare: listaStari,
  banca: listaText,
  valuta: valute,
  categ: listaText,
  /** Cautare libera, insensibila la diacritice. */
  q: text,
  /** Prag minim de suma, in unitati majore (se converteste la minor la filtrare). */
  min: numar,
  sort: z
    .enum(['furnizor', 'suma', 'stare', 'banca', 'facturi', 'sold', 'stoc'])
    .optional()
    .catch(undefined),
  /** Coloanele afisate. Lipsa = implicitele din `coloane.ts`. */
  col: listaText,
  grup: z.enum(['niciuna', 'banca', 'categorie', 'stare']).optional().catch(undefined),
  dens: densitate,
  /** Selectia, ca sa poata fi trimisa prin link impreuna cu bara de actiuni in bloc. */
  sel: listaText,
  /** Drawer deep-linkabil: id-ul platii deschise. */
  drawer: text,
});

/** `/matrice` — ancora, orizontul si ce se ascunde. */
const cautareMatrice = z.object({
  /** Ziua pe care e centrata matricea, `YYYY-MM-DD`. */
  ancora: text,
  /** Cate zile lucratoare se arata. */
  zile: numar,
  stare: listaStari,
  /** Ascunde coloanele `sold week` / `stoc week` (snapshot-uri, nu plati). */
  ascundeSoldStoc: boolean,
  dens: densitate,
});

const cautareValutar = z.object({
  /** Tranzactia deschisa in drawer. */
  drawer: text,
  valuta: valute,
});

const cautareCashflow = z.object({
  de: text,
  pana: text,
  entitate: z.enum(['EVO', 'EDL', 'TECHV', 'toate']).optional().catch(undefined),
});

const cautareFurnizori = z.object({
  q: text,
  categ: listaText,
  /**
   * Filtru de calitate a datelor — fostul badge `Control` din culoarea `6FA8DC` a foii, largit.
   * Vocabularul e cel al rutei (`SuppliersQuery.quality`), nu unul paralel: doua liste ar diverge
   * la prima verificare adaugata, iar URL-ul ar trimite un filtru pe care serverul nu-l cunoaste.
   */
  control: z
    .enum([
      'needs_review',
      'iban_invalid',
      'iban_flagged',
      'cui_missing',
      'cui_check_failed',
      'name_mismatch',
      'name_mojibake',
    ])
    .optional()
    .catch(undefined),
});

const cautareJurnal = z.object({
  /** Codul actiunii, sau un PREFIX terminat in punct (`payment_entry.`). */
  actiune: text,
  /** `app_user.id` al autorului. */
  autor: z.coerce.number().int().positive().optional().catch(undefined),
  de: text,
  pana: text,
  q: text,
  pag: z.coerce.number().int().min(1).optional().catch(undefined),
});

const cautareActivitate = z.object({
  q: text,
  autor: text,
  fel: z.enum(['comentariu', 'factura', 'regula', 'audit']).optional().catch(undefined),
});

// --- arborele de rute --------------------------------------------------------------------------

const rootRoute = createRootRoute({ component: Shell });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    // „Azi" se calculeaza mereu in fusul Bucurestiului; un `new Date()` naiv ar amana platile de
    // vineri pe sambata.
    throw redirect({ to: '/zi/$data', params: { data: todayInBucharest() }, replace: true });
  },
});

const ziRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/zi/$data',
  validateSearch: (search: Record<string, unknown>) => cautareZi.parse(search),
  component: PlanPlatiZi,
});

const matriceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/matrice',
  validateSearch: (search: Record<string, unknown>) => cautareMatrice.parse(search),
  component: lazyRouteComponent(
    () => import('./features/matrice/VedereMatrice.tsx'),
    'VedereMatrice',
  ),
});

const cashflowRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cashflow',
  validateSearch: (search: Record<string, unknown>) => cautareCashflow.parse(search),
  component: lazyRouteComponent(() => import('./features/cashflow/Cashflow.tsx'), 'Cashflow'),
});

const valutarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/valutar/$data',
  validateSearch: (search: Record<string, unknown>) => cautareValutar.parse(search),
  component: lazyRouteComponent(() => import('./features/valutar/Comparator.tsx'), 'Comparator'),
});

const furnizoriRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/furnizori',
  validateSearch: (search: Record<string, unknown>) => cautareFurnizori.parse(search),
  component: lazyRouteComponent(() => import('./features/furnizori/Furnizori.tsx'), 'Furnizori'),
});

/**
 * `$id` e cheia SUROGAT, nu CUI-ul.
 *
 * Planul scria `/furnizori/:cui`, dar D5 a aratat de ce nu se poate: 85 de furnizori nu au deloc
 * CUI (sunt galeti de cost — `Altele`, `recrutare HR`), iar `RO29704683` si `RO37608172` apar
 * fiecare pe doua firme diferite. Un URL construit pe CUI ar fi ori imposibil de format, ori
 * ambiguu — exact cele doua moduri in care un link trimis intre colege duce pe randul gresit.
 * CUI-ul ramane cautabil in `/furnizori?q=`, unde ambiguitatea se vede si se alege.
 */
const furnizorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/furnizori/$id',
  component: lazyRouteComponent(
    () => import('./features/furnizori/FurnizorDetaliu.tsx'),
    'FurnizorDetaliu',
  ),
});

const stocuriRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stocuri',
  component: lazyRouteComponent(() => import('./features/stocuri/Stocuri.tsx'), 'Stocuri'),
});

const comisioaneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/comisioane',
  component: lazyRouteComponent(() => import('./features/comisioane/Comisioane.tsx'), 'Comisioane'),
});

const activitateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activitate',
  validateSearch: (search: Record<string, unknown>) => cautareActivitate.parse(search),
  component: lazyRouteComponent(() => import('./features/activitate/Activitate.tsx'), 'Activitate'),
});

const importExportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/import-export',
  component: lazyRouteComponent(
    () => import('./features/import-export/ImportExport.tsx'),
    'ImportExport',
  ),
});

const raportRoute = createRoute({
  getParentRoute: () => rootRoute,
  /** `perioada` are forma `2026-W34`. */
  path: '/raport/$perioada',
  component: lazyRouteComponent(
    () => import('./features/raport/RaportSaptamanal.tsx'),
    'RaportSaptamanal',
  ),
});

const reconciliereRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reconciliere',
  component: lazyRouteComponent(
    () => import('./features/reconciliere/Reconciliere.tsx'),
    'Reconciliere',
  ),
});

const jurnalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jurnal',
  validateSearch: (search: Record<string, unknown>) => cautareJurnal.parse(search),
  component: lazyRouteComponent(() => import('./features/jurnal/Jurnal.tsx'), 'Jurnal'),
});

const setariRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setari',
  component: lazyRouteComponent(() => import('./features/setari/Setari.tsx'), 'Setari'),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  ziRoute,
  matriceRoute,
  cashflowRoute,
  valutarRoute,
  furnizoriRoute,
  furnizorRoute,
  stocuriRoute,
  comisioaneRoute,
  activitateRoute,
  importExportRoute,
  raportRoute,
  reconciliereRoute,
  jurnalRoute,
  setariRoute,
]);

export const router = createRouter({
  routeTree,
  /** Prefetch la intentie: mouse-ul deasupra unei rute o incarca inainte de click. */
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
